// Timesheet export helpers — PDF, CSV, Excel. Operates on the currently
// filtered rows passed in (each is a ws_time_entries row + joined fields).
//
// The files show the entries exactly as the Time entries list does: newest
// day first, one row per piece of work per day (repeat sessions of the same
// task collapse into one row, like the list's "3 sessions"), the start – end
// span in the viewer's zone, and durations as decimal hours ("1.683") plus
// whole minutes ("101"), not "1h 41m", so spreadsheets can sum them.
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { localDateStr, fmtDate, fmtDuration, fmtTime, utcNaiveToDate } from "../wsConstants";

// Entries for the same piece of work on a day — same owner, project, task,
// description and billable flag — are one row. Shared with the Timesheets list
// (pages/Workspace/Timesheets.js) so the screen and the files can't disagree.
export const timesheetGroupKey = (e) =>
  [e.user_id, e.project_id || 0, e.task_id || 0, e.description || "", e.billable ? 1 : 0].join("|");

const COLUMNS = [
  { key: "date", label: "Date" },
  { key: "employee", label: "Employee" },
  { key: "project", label: "Project" },
  { key: "task", label: "Task" },
  { key: "description", label: "Description" },
  { key: "time", label: "Time" },
  { key: "sessions", label: "Sessions" },
  { key: "hours", label: "Hours" },
  { key: "minutes", label: "Minutes" },
  { key: "billable", label: "Billable" },
];

// Earliest start – latest end of the timed sessions; "" for duration-only.
const spanOf = (items) => {
  const timed = items.filter((e) => e.started_at && e.ended_at);
  if (!timed.length) return "";
  const first = timed.reduce((a, b) => (utcNaiveToDate(b.started_at) < utcNaiveToDate(a.started_at) ? b : a));
  const last = timed.reduce((a, b) => (utcNaiveToDate(b.ended_at) > utcNaiveToDate(a.ended_at) ? b : a));
  return `${fmtTime(first.started_at)} – ${fmtTime(last.ended_at)}`;
};

// Minutes → decimal hours to 3 places, as a number (353 → 5.883, 120 → 2).
// Text outputs (CSV, PDF) print it with fmtHours so it always shows 3 places.
const toHours = (mins) => Math.round((mins / 60) * 1000) / 1000;
const fmtHours = (h) => Number(h).toFixed(3);
const HOURS_COL = COLUMNS.findIndex((c) => c.key === "hours");
const textCell = (r, c) => (c.key === "hours" ? fmtHours(r.hours) : r[c.key]);

// Entries → display rows, in the list's order: days newest first (the order
// they arrive in), groups in order of their first entry.
export const toTimesheetRows = (entries) => {
  const days = new Map();
  for (const e of entries) {
    const day = String(e.entry_date || "").slice(0, 10);
    if (!days.has(day)) days.set(day, new Map());
    const groups = days.get(day);
    const k = timesheetGroupKey(e);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(e);
  }
  const rows = [];
  for (const day of [...days.keys()].sort((a, b) => (a < b ? 1 : -1))) {
    for (const items of days.get(day).values()) {
      const e = items[0];
      const mins = items.reduce((s, x) => s + (Number(x.duration_minutes) || 0), 0);
      rows.push({
        date: fmtDate(day),
        employee: e.username || "",
        project: e.project_name || "Global",
        task: e.task_title || "",
        description: e.description || "",
        time: spanOf(items),
        sessions: items.length,
        duration: fmtDuration(mins),
        billable: e.billable ? "Billable" : "Internal",
        hours: toHours(mins),
        minutes: mins,
      });
    }
  }
  return rows;
};

const totalMinutes = (entries) => entries.reduce((s, e) => s + (Number(e.duration_minutes) || 0), 0);

// Local day (toISOString is UTC and would name a late-evening export with
// the wrong date).
const fileStamp = () => localDateStr();

export function downloadTimesheetCSV(entries) {
  const rows = toTimesheetRows(entries);
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = COLUMNS.map((c) => c.label).join(",");
  const body = rows.map((r) => COLUMNS.map((c) => esc(textCell(r, c))).join(",")).join("\n");
  const mins = totalMinutes(entries);
  const total = `${COLUMNS.slice(0, -4).map(() => "").join(",")},Total,${fmtHours(toHours(mins))},${mins},`;
  // BOM so Excel opening the CSV reads "–" and "·" as UTF-8, not mojibake.
  const blob = new Blob([`﻿${header}\n${body}\n${total}`], { type: "text/csv;charset=utf-8;" });
  triggerDownload(blob, `timesheet-${fileStamp()}.csv`);
}

export function downloadTimesheetExcel(entries) {
  const rows = toTimesheetRows(entries);
  const mins = totalMinutes(entries);
  const aoa = [
    COLUMNS.map((c) => c.label),
    ...rows.map((r) => COLUMNS.map((c) => r[c.key])),
    [...COLUMNS.slice(0, -4).map(() => ""), "Total", toHours(mins), mins, ""],
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws["!cols"] = [12, 16, 20, 18, 34, 18, 9, 9, 10, 10].map((wch) => ({ wch }));
  // Keep hours numeric (summable) but always display 3 decimals.
  for (let i = 1; i < aoa.length; i++) {
    const cell = ws[XLSX.utils.encode_cell({ r: i, c: HOURS_COL })];
    if (cell && cell.t === "n") cell.z = "0.000";
  }
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Timesheet");
  XLSX.writeFile(wb, `timesheet-${fileStamp()}.xlsx`);
}

// jsPDF's built-in font only covers WinAnsi: characters outside it (an arrow,
// the narrow no-break space some browsers put before AM/PM) print as garbage
// and space the whole string out. Swap them for plain equivalents.
const pdfSafe = (v) =>
  String(v ?? "")
    .replace(/[–—−]/g, "-")
    .replace(/…/g, "...")
    .replace(/[   ]/g, " ");

// meta.from / meta.to — YYYY-MM-DD of the filter range.
export function downloadTimesheetPDF(entries, meta = {}) {
  const rows = toTimesheetRows(entries);
  const doc = new jsPDF({ orientation: "landscape" });
  doc.setFontSize(14);
  doc.text("Timesheet", 14, 16);
  doc.setFontSize(9);
  // Plain "to": jsPDF's built-in font has no "→" — it printed garbage and
  // spread the whole line out.
  const range = meta.from || meta.to ? `${fmtDate(meta.from) || "..."} to ${fmtDate(meta.to) || "..."}` : "";
  const sub = [
    range,
    `${entries.length} entr${entries.length === 1 ? "y" : "ies"}`,
    `${fmtHours(toHours(totalMinutes(entries)))} h (${totalMinutes(entries)} min) total`,
  ]
    .filter(Boolean)
    .join("  ·  ");
  doc.text(pdfSafe(sub), 14, 22);
  autoTable(doc, {
    startY: 26,
    head: [COLUMNS.map((c) => c.label)],
    body: rows.map((r) => COLUMNS.map((c) => pdfSafe(textCell(r, c)))),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [245, 75, 1] },
    columnStyles: { 6: { halign: "center" }, 7: { halign: "right" }, 8: { halign: "right" } },
  });
  doc.save(`timesheet-${fileStamp()}.pdf`);
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
